import api from 'helpers/api'
import { MAX_HUB_FOLDER_MOVE_ITEMS } from 'app-constants'
import { useApplicationStore } from 'hooks/application'
import { useProject } from 'hooks/useProject'
import { IHubFolder, IProject, ISidebarMoveItem, ISidebarResource } from 'types'
import { applyMove, countResources, moveBatches, resourceKey } from 'components/sidebar/components/folders/tree'
import { publishResourceChange } from 'helpers/resourceChanges'
import { sendOwnWrite } from 'helpers/realtimeOrigin'
import { getErrorSupportMetadata } from 'helpers/apiError'

/** How a move went. A refused one carries the server's reason when it gave one. */
export type MoveResult = { ok: true } | { ok: false; reason: string | null }

/** Each hub's last queued move request, settled either way, for the next one to wait on. */
const moveQueues = new Map<string, Promise<void>>()

/**
 * Sends `send` once every move queued before it for `hubId` has settled, however it went, so the
 * server takes a hub's moves in the order they were made: each one's index was worked out on the tree
 * as the move before it left it. The first move in an idle queue is sent at once. Settles as `send` does.
 */
const queueMove = <T>(hubId: string, send: () => Promise<T>): Promise<T> => {
  const previous = moveQueues.get(hubId)
  const request = previous ? previous.then(send) : send()
  const settled = request.then(
    () => undefined,
    () => undefined
  )
  moveQueues.set(hubId, settled)
  // Nothing queued behind it: the hub's queue is idle again.
  settled.then(() => {
    if (moveQueues.get(hubId) === settled) moveQueues.delete(hubId)
  })
  return request
}

/** The message an API error carries, if any. */
const errorReason = (error: unknown): string | null => {
  const message = error instanceof Object ? (error as { message?: unknown }).message : undefined
  return typeof message === 'string' && message ? message : null
}

/** A document or table a batch archive couldn't archive, with the server's reason when it gave one. */
export interface ArchiveFailure {
  resource: ISidebarResource
  reason: string | null
}

export interface HubFolderActions {
  /**
   * Move items under `parentFolderId` (null = top level) at `index` (null = append). Never rejects:
   * a refusal has already been shown and undone when it resolves.
   */
  moveItems: (items: ISidebarMoveItem[], parentFolderId: string | null, index: number | null) => Promise<MoveResult>
  /** The created folder, or null when the server refused (the error has been shown). */
  createFolder: (name: string, parentId: string | null) => Promise<IHubFolder | null>
  renameFolder: (folderId: string, name: string) => Promise<void>
  /** Rejects, after showing the error, when the server refuses, so a confirm dialog can stay open. */
  deleteFolder: (folderId: string) => Promise<void>
  /**
   * Renames a document or table. Never rejects: a refusal puts the old name back and shows why. An open
   * page showing it is told the new name once the server has it.
   */
  renameResource: (resource: ISidebarResource, name: string) => Promise<void>
  /**
   * Archives a document or table and takes its row out of the sidebar. Rejects, after showing the
   * error, when the server refuses, so a confirm dialog can stay open.
   */
  archiveResource: (resource: ISidebarResource) => Promise<void>
  /**
   * Archives several documents and tables one after another, taking each row out of the sidebar as it
   * goes, and carrying on past a refusal. Resolves with the ones that were refused, after one message
   * saying how it went. Never rejects.
   */
  archiveResources: (resources: ISidebarResource[]) => Promise<ArchiveFailure[]>
}

/** Adds `folder` unless a socket refresh has already brought it in. */
const withFolder = (prev: IProject, folder: IHubFolder): IProject =>
  prev.hubFolders.some((f) => f.publicId === folder.publicId)
    ? prev
    : { ...prev, hubFolders: [...prev.hubFolders, folder] }

/** Where a folder, document or table sits: the folder it's in (null = top level) and its place there. */
interface Placement {
  parent: string | null
  position: number
}

interface PlacementChange {
  from: Placement
  to: Placement
}

/** What a move changed, by folder id and by resource kind and id: the items moved and the ones renumbered. */
interface MoveChanges {
  folders: Map<string, PlacementChange>
  resources: Map<string, PlacementChange>
}

const NO_CHANGES: MoveChanges = { folders: new Map(), resources: new Map() }

const folderPlacement = (f: IHubFolder): Placement => ({ parent: f.parentId, position: f.position })
const resourcePlacement = (r: ISidebarResource): Placement => ({ parent: r.folderId, position: r.position })
const samePlacement = (a: Placement, b: Placement): boolean => a.parent === b.parent && a.position === b.position

/** The placements that differ between `before` and `after`, which applyMove keeps in the same order. */
const changedPlacements = <T>(
  before: T[],
  after: T[],
  key: (item: T) => string,
  placement: (item: T) => Placement
): Map<string, PlacementChange> => {
  const changes = new Map<string, PlacementChange>()
  after.forEach((item, i) => {
    const from = placement(before[i])
    const to = placement(item)
    if (!samePlacement(from, to)) changes.set(key(item), { from, to })
  })
  return changes
}

const moveChanges = (prev: IProject, next: ReturnType<typeof applyMove>): MoveChanges => ({
  folders: changedPlacements(prev.hubFolders, next.folders, (f) => f.publicId, folderPlacement),
  resources: changedPlacements(prev.sidebarResources, next.resources, resourceKey, resourcePlacement)
})

/**
 * Puts back what a move changed. Only a placement that still reads what the move set is put back:
 * one that has changed since (a refresh, a later move) is left as it now is.
 */
const undoMove = (prev: IProject, changes: MoveChanges): IProject => ({
  ...prev,
  hubFolders: prev.hubFolders.map((f) => {
    const change = changes.folders.get(f.publicId)
    if (!change || !samePlacement(folderPlacement(f), change.to)) return f
    return { ...f, parentId: change.from.parent, position: change.from.position }
  }),
  sidebarResources: prev.sidebarResources.map((r) => {
    const change = changes.resources.get(resourceKey(r))
    if (!change || !samePlacement(resourcePlacement(r), change.to)) return r
    return { ...r, folderId: change.from.parent, position: change.from.position }
  })
})

const withFolderName = (prev: IProject, folderId: string, name: string): IProject => ({
  ...prev,
  hubFolders: prev.hubFolders.map((f) => (f.publicId === folderId ? { ...f, name } : f))
})

/**
 * Names the resource with this key `name`. With `unless`, only while its name is still `unless`, so
 * putting a name back leaves alone one that has changed since (a refresh brought a newer one).
 */
const withResourceName = (prev: IProject, key: string, name: string, unless?: string): IProject => ({
  ...prev,
  sidebarResources: prev.sidebarResources.map((r) =>
    resourceKey(r) === key && (unless === undefined || r.name === unless) ? { ...r, name } : r
  )
})

const withoutResource = (prev: IProject, key: string): IProject => ({
  ...prev,
  sidebarResources: prev.sidebarResources.filter((r) => resourceKey(r) !== key)
})

/**
 * Folder operations for the current hub, and renaming and archiving its documents and tables. Moves
 * and renames show immediately and snap back, with the server's error, if it refuses them. A hub's move
 * requests go to the server one at a time, in the order they were made.
 *
 * Each request names this tab (sendOwnWrite), so the `resources_updated` event it causes is skipped
 * here: what it changed is already on screen.
 *
 * Every action records its hub when called. A result that arrives after the user has switched hub
 * changes nothing on screen and triggers no refresh.
 *
 * Written as promise chains rather than try/catch: the React Compiler can't yet compile conditionals
 * inside a try block.
 */
export const useHubFolders = (): HubFolderActions => {
  const { project, projectOwner, updateProject, refreshSidebar } = useProject()
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()

  /** Applies `update` only while `hubId` is still the hub on screen. */
  const updateHub = (hubId: string, update: (prev: IProject) => IProject): void =>
    updateProject((prev) => (prev.publicId === hubId ? update(prev) : prev))

  /** One move request, of at most MAX_HUB_FOLDER_MOVE_ITEMS items with none inside another. */
  const moveBatch: HubFolderActions['moveItems'] = (items, parentFolderId, index) => {
    const hubId = project.publicId
    // What this move changed, recorded as it's applied. A failure undoes only that, on whatever is on
    // screen by then, so a folder created or renamed meanwhile, or another move, survives. The
    // rollback's update is queued after this one, so the record is always there when it runs.
    let changes: MoveChanges = NO_CHANGES
    updateHub(hubId, (prev) => {
      const next = applyMove(prev.hubFolders, prev.sidebarResources, items, parentFolderId, index)
      changes = moveChanges(prev, next)
      return { ...prev, hubFolders: next.folders, sidebarResources: next.resources }
    })
    // Shown at once, but sent only after the hub's earlier moves have settled (queueMove). Counted as
    // an own write from now, so a sidebar read made while it waits is caught up after it lands.
    return sendOwnWrite(hubId, (headers) =>
      queueMove(hubId, () => api.moveHubFolderItems(hubId, { items, parentFolderId, index }, headers))
    ).then(
      (): MoveResult => {
        // Only a hub Owner's sidebar lists every folder. Anyone else's lists only folders with something
        // in them they can open, so the server's sidebar is read back to drop a folder the move emptied.
        if (!projectOwner) refreshSidebar(hubId)
        return { ok: true }
      },
      (error): MoveResult => {
        updateHub(hubId, (prev) => undoMove(prev, changes))
        displayErrorMessage(error)
        refreshSidebar(hubId)
        return { ok: false, reason: errorReason(error) }
      }
    )
  }

  /**
   * More than the server takes in one move goes in batches of MAX_HUB_FOLDER_MOVE_ITEMS (moveBatches),
   * each sent once the one before has landed, so together they land where the single move would.
   * Stops at the first refusal, which undoes only its own batch.
   */
  const moveItems: HubFolderActions['moveItems'] = async (requested, parentFolderId, index) => {
    const batches = moveBatches(
      project.hubFolders,
      project.sidebarResources,
      requested,
      parentFolderId,
      index,
      MAX_HUB_FOLDER_MOVE_ITEMS
    )
    for (const batch of batches) {
      const result = await moveBatch(batch.items, parentFolderId, batch.index)
      if (!result.ok) return result
    }
    return { ok: true }
  }

  const createFolder: HubFolderActions['createFolder'] = (name, parentId) => {
    const hubId = project.publicId
    return sendOwnWrite(hubId, (headers) => api.createHubFolder(hubId, { name, parentId }, headers)).then(
      (response) => {
        updateHub(hubId, (prev) => withFolder(prev, response.data))
        return response.data
      },
      (error) => {
        displayErrorMessage(error)
        return null
      }
    )
  }

  const renameFolder: HubFolderActions['renameFolder'] = (folderId, name) => {
    const hubId = project.publicId
    const previous = project.hubFolders.find((f) => f.publicId === folderId)
    updateHub(hubId, (prev) => withFolderName(prev, folderId, name))
    return sendOwnWrite(hubId, (headers) => api.renameHubFolder(hubId, folderId, name, headers)).then(
      // The server trims the name; show what it stored.
      (response) => updateHub(hubId, (prev) => withFolderName(prev, folderId, response?.data?.name || name)),
      (error) => {
        if (previous) updateHub(hubId, (prev) => withFolderName(prev, folderId, previous.name))
        displayErrorMessage(error)
        refreshSidebar(hubId)
      }
    )
  }

  const deleteFolder: HubFolderActions['deleteFolder'] = (folderId) => {
    const hubId = project.publicId
    return sendOwnWrite(hubId, (headers) => api.deleteHubFolder(hubId, folderId, headers)).then(
      // Everything in the folder moved up to its parent server-side; read the result back.
      () => refreshSidebar(hubId),
      (error) => {
        displayErrorMessage(error)
        throw error
      }
    )
  }

  const renameResource: HubFolderActions['renameResource'] = (resource, name) => {
    const hubId = project.publicId
    const { kind, publicId: id } = resource
    const key = resourceKey(resource)
    updateHub(hubId, (prev) => withResourceName(prev, key, name))
    const context = { projectId: hubId }
    const request = sendOwnWrite(hubId, (headers) =>
      kind === 'document'
        ? api.updateProcess({ processId: id, name, context }, headers)
        : api.updateTable(id, { name, context }, headers)
    )
    return request.then(
      (response) => {
        // The server may tidy the name; show what it stored.
        const saved: string = response?.data?.name || name
        updateHub(hubId, (prev) => withResourceName(prev, key, saved))
        publishResourceChange({ kind, id, change: 'renamed', name: saved })
      },
      (error) => {
        updateHub(hubId, (prev) => withResourceName(prev, key, resource.name, name))
        displayErrorMessage(error)
        refreshSidebar(hubId)
      }
    )
  }

  /** Archives one document or table and takes its row out; rejects with the server's error, showing nothing. */
  const archiveOne = (hubId: string, resource: ISidebarResource): Promise<void> => {
    const { kind, publicId: id } = resource
    const params = { context: { projectId: hubId } }
    const request = sendOwnWrite(hubId, (headers) =>
      kind === 'document' ? api.deleteProcess(id, params, headers) : api.deleteTable(id, params, headers)
    )
    return request.then(() => {
      updateHub(hubId, (prev) => withoutResource(prev, resourceKey(resource)))
      publishResourceChange({ kind, id, change: 'archived' })
    })
  }

  const archiveResource: HubFolderActions['archiveResource'] = (resource) =>
    archiveOne(project.publicId, resource).then(
      () => setSnackbarMessage({ status: 'success', message: `'${resource.name}' has been archived.` }),
      (error) => {
        displayErrorMessage(error)
        throw error
      }
    )

  const archiveResources: HubFolderActions['archiveResources'] = async (resources) => {
    // Nothing to archive (an empty folder's delete), so nothing to say.
    if (resources.length === 0) return []
    const hubId = project.publicId
    const failures: ArchiveFailure[] = []
    // The first failure the API couldn't explain, which the message offers AI Support for.
    let unexplained: unknown = null
    for (const resource of resources) {
      await archiveOne(hubId, resource).catch((error: unknown) => {
        failures.push({ resource, reason: errorReason(error) })
        if (unexplained === null && getErrorSupportMetadata(error).offerSupport) unexplained = error
      })
    }
    const refused = new Set(failures.map((f) => f.resource))
    const archived = resources.filter((r) => !refused.has(r))
    if (failures.length === 0) {
      setSnackbarMessage({ status: 'success', message: `${countResources(archived)} archived.` })
    } else {
      const notArchived = `${countResources([...refused])} couldn't be archived.`
      const message = archived.length > 0 ? `${countResources(archived)} archived. ${notArchived}` : notArchived
      setSnackbarMessage({
        status: 'error',
        message,
        ...(unexplained === null ? {} : getErrorSupportMetadata(unexplained))
      })
    }
    return failures
  }

  return { moveItems, createFolder, renameFolder, deleteFolder, renameResource, archiveResource, archiveResources }
}

export default useHubFolders
